import { useCallback, useContext, useEffect, useRef, useState } from "react";
import { MapTypeId } from "core/features/maps/enums";
import { LatLong } from "core/features/maps/typings";
import { TalhoesContext } from "core/features/talhoes/contexts/Talhoes.context";
import { GeoJsonProps, TalhoesProps } from "core/features/talhoes/typings/listaEGeojson";

interface UseMapFilterProps {
  currentItem?: TalhoesProps;
  geojson?: GeoJsonProps;
  defaultPontoCentral: LatLong;
}

interface Path {
  lat: number;
  lng: number;
}

interface PolygonsPath {
  id: number;
  paths: Path[];
}

export const useMapFilter = ({
  currentItem,
  geojson,
  defaultPontoCentral,
}: UseMapFilterProps) => {
  const [map, setMap] = useState<google.maps.Map | null>(null);
  const [canEdit, setCanEdit] = useState<boolean>(false);
  const [canMove, setCanMove] = useState<boolean>(false);
  const [polygonsPath, setPolygonsPath] = useState<PolygonsPath[]>();
  const [theresChanges, setTheresChanges] = useState<boolean>(false);

  const polygonRef = useRef<Record<string, google.maps.Polygon>>({});

  const {
    pathsToSave,
    importKMLCode,
    setImportKMLCode,
    setPathsToSave,
  } = useContext(TalhoesContext);

  const currentItemGeojson = geojson?.features.find((item) => {
    return item.properties.id === currentItem?.talhaoId;
  });

  const onLoad = (mapToSet: google.maps.Map) => {
    setMap(mapToSet);
  };

  const onUnmountMap = (mapToSet: google.maps.Map) => {
    setMap(null);
  };

  const onUnmountPolygon = () => {
    polygonRef.current = {};
  };

  const onLoadPolygon = (polygon: any, id: number) => {
    polygonRef.current[id] = polygon;
  };

  const onPolygonEdit = (id: number) => {
    if (polygonRef.current[id]) {
      setImportKMLCode(false);
      setTheresChanges(true);
      const nextPath = polygonRef.current[id]
        .getPath()
        .getArray()
        .map((latLng: any) => {
          return { lat: latLng.lat(), lng: latLng.lng() };
        });
      const polygons = polygonsPath?.map(polygon => {
        if (polygon.id === id) {
          return { id: polygon.id, paths: nextPath }
        }
        return polygon;
      });
      setPolygonsPath(polygons);
    }
  };

  const handleCreatePolygon = (props: LatLong) => {
    setImportKMLCode(false);

    const lastPolygon = polygonsPath ? polygonsPath[polygonsPath?.length - 1] : null;
    const canCreateOtherPolygon = polygonsPath && lastPolygon && lastPolygon?.paths.length >= 4;

    if (canCreateOtherPolygon) {
      const polygon = {
        id: Math.random(),
        paths: [
          {
            lat: props.lat,
            lng: props.long,
          },
        ],
      }
      setPolygonsPath([...polygonsPath, polygon]);
      return;
    }

    setTheresChanges(true);

    if (polygonsPath && lastPolygon) {
      const polygon = {
        id: lastPolygon.id,
        paths: [
          ...lastPolygon.paths,
          {
            lat: props.lat,
            lng: props.long,
          },
        ]
      }
      const polygons = polygonsPath.slice(0, -1);
      setPolygonsPath([...polygons, polygon]);
    } else {
      const polygon = {
        id: Math.random(),
        paths: [
          {
            lat: props.lat,
            lng: props.long,
          },
        ],
      }
      setPolygonsPath([polygon]);
    }
  };

  const getPolygonCoordinates = useCallback(() => {
    const polygons = currentItemGeojson?.geometry.coordinates;
    const type = currentItemGeojson?.geometry.type;

    if (type === "MultiPolygon") {
      return polygons?.flatMap(polygon => {
        return polygon.map(coords => {
          return coords.map(([lng, lat]: any) => {
            return { lat, lng }
          });
        });
      });
    }

    return [
      polygons?.[0]?.map(([lng, lat]: any) => ({
        lat,
        lng,
      })),
    ];
  }, [currentItemGeojson]);

  useEffect(() => {
    if (map) {
      map.setMapTypeId(MapTypeId.SATELLITE);
      map.setZoom(11.5);
      map.setCenter({
        lat: defaultPontoCentral.lat,
        lng: defaultPontoCentral.long,
      });

      map.data.setStyle((feature) => {
        const id = feature.getProperty("id");
        const shouldRemove = id === currentItem?.talhaoId;

        if (shouldRemove) {
          return {
            opacity: 0,
            fillOpacity: 0,
            strokeOpacity: 0,
          };
        }

        const fill = feature.getProperty("fill");
        const fillOpacity = feature.getProperty("fillOpacity");
        const stroke = feature.getProperty("stroke");
        const strokeOpacity = feature.getProperty("strokeOpacity");
        const strokeWidth = feature.getProperty("strokeWidth");

        return {
          opacity: fillOpacity,
          fillColor: fill,
          fillOpacity,
          strokeColor: stroke,
          strokeOpacity,
          strokeWidth,
        };
      });

      if (geojson) {
        map.data.addGeoJson(geojson!);
      }
    }

    return () => {
      map?.data.forEach((item) => {
        map.data.remove(item);
      });
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [map, geojson, currentItem]);

  useEffect(() => {
    const coords = getPolygonCoordinates();
    const validCoords = (coords ?? []).filter(paths => paths !== undefined) as Path[][];
    const polygons = coords?.map(paths => {
      return {
        id: Math.random(),
        paths: paths ?? [],
      }
    });
    setPolygonsPath(polygons);
    setPathsToSave(validCoords);
    setTheresChanges(false);
    map?.setZoom(14);
    if (currentItem && currentItemGeojson) {
      map?.setCenter({
        lat: currentItemGeojson.properties.pontoCentralLat!,
        lng: currentItemGeojson.properties.pontoCentralLong!,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentItem, currentItemGeojson, getPolygonCoordinates]);

  useEffect(() => {
    const shouldRemoveMark =
      geojson?.features.find((item) => item.id === currentItem?.codigo) ===
      undefined;

    if (shouldRemoveMark) {
      setPolygonsPath(undefined);
    }
  }, [currentItem, geojson]);

  useEffect(() => {
    if (currentItem && pathsToSave?.length && importKMLCode) {
      map?.setCenter({
        lat: pathsToSave[0][0].lat,
        lng: pathsToSave[0][0].lng,
      });
      map?.setZoom(15);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentItem, pathsToSave]);

  return {
    items: {
      polygonsPath,
      canEdit,
      canMove,
      map,
      theresChanges,
    },
    onChange: {
      onPolygonEdit,
      changeCanEdit: setCanEdit,
      changeCanMove: setCanMove,
      handleCreatePolygon,
      setPolygonsPath,
      setTheresChanges,
    },
    loaders: {
      onLoadPolygon,
      onLoad,
      onUnmountPolygon,
      onUnmountMap,
    },
  };
};
